import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group cursor-pointer">
              <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                <i className="ri-heart-pulse-line text-white text-2xl"></i>
              </div>
              <span className={`text-2xl font-bold transition-colors duration-300 ${
                isScrolled ? "text-slate-900" : "text-white"
              }`}>
                HealthLeads
              </span>
            </Link>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center gap-8">
              <a
                href="#about"
                className={`text-sm font-medium transition-colors duration-300 hover:text-emerald-500 cursor-pointer ${
                  isScrolled ? "text-slate-700" : "text-white/90"
                }`}
              >
                Über uns
              </a>
              <a
                href="#target-groups"
                className={`text-sm font-medium transition-colors duration-300 hover:text-emerald-500 cursor-pointer ${
                  isScrolled ? "text-slate-700" : "text-white/90"
                }`}
              >
                Zielgruppen
              </a>
              <a
                href="#features"
                className={`text-sm font-medium transition-colors duration-300 hover:text-emerald-500 cursor-pointer ${
                  isScrolled ? "text-slate-700" : "text-white/90"
                }`}
              >
                Features
              </a>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-4">
              <Link
                to="/login"
                className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  isScrolled
                    ? "text-slate-700 hover:bg-slate-100"
                    : "text-white hover:bg-white/10"
                }`}
              >
                Anmelden
              </Link>
              <button
                onClick={() => setShowApplicationModal(true)}
                className="px-5 py-2.5 bg-emerald-500 text-white text-sm font-semibold rounded-lg hover:bg-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                Jetzt Registrieren
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Bewerbungsformular Modal */}
      {showApplicationModal && (
        <ApplicationModal onClose={() => setShowApplicationModal(false)} />
      )}
    </>
  );
}

interface ApplicationModalProps {
  onClose: () => void;
}

function ApplicationModal({ onClose }: ApplicationModalProps) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    sponsorName: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const formBody = new URLSearchParams({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        sponsorName: formData.sponsorName,
        message: formData.message
      });

      const response = await fetch("https://readdy.ai/api/form/d5flc40f0tcnoht8700g", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Partner-Bewerbung</h2>
            <p className="text-sm text-slate-600 mt-1">Werde Partner unseres Eqology Leadsystems</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl text-slate-600"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} data-readdy-form id="eqology-application-form" className="px-8 py-6">
          <div className="space-y-6">
            {/* Persönliche Daten */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Persönliche Daten</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Vorname <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    placeholder="Max"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nachname <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    placeholder="Mustermann"
                  />
                </div>
              </div>
            </div>

            {/* Kontaktdaten */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Kontaktdaten</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    E-Mail-Adresse <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    placeholder="max@beispiel.de"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Telefonnummer <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    placeholder="+49 123 456789"
                  />
                </div>
              </div>
            </div>

            {/* Sponsor-Informationen */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Sponsor-Informationen</h3>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Name des Eqology-Sponsors <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="sponsorName"
                  required
                  value={formData.sponsorName}
                  onChange={(e) => setFormData({ ...formData, sponsorName: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  placeholder="Name Ihres Sponsors"
                />
              </div>
            </div>

            {/* Nachricht */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Nachricht (optional)
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, message: e.target.value });
                  }
                }}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm resize-none"
                placeholder="Erzählen Sie uns etwas über sich und Ihre Motivation..."
              />
              <p className="text-xs text-slate-500 mt-1">{formData.message.length}/500 Zeichen</p>
            </div>

            {/* Status Messages */}
            {submitStatus === "success" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-emerald-500 text-xl"></i>
                <p className="text-sm text-emerald-700">
                  Vielen Dank für Ihre Bewerbung! Wir werden Ihre Angaben überprüfen und uns in Kürze bei Ihnen melden.
                </p>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                <i className="ri-error-warning-fill text-red-500 text-xl"></i>
                <p className="text-sm text-red-700">
                  Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex items-center gap-4 pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Wird gesendet...
                  </span>
                ) : (
                  "Bewerbung absenden"
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
